import type {
  VendorRegisterRequest, VendorRegisterResponse,
  VendorLoginRequest, VendorLoginResponse,
  VendorSocialLoginRequest, VendorSocialLoginResponse,
  VerifyOtpRequest, VendorVerifyOtpResponse,
  SendOtpRequest, ResendOtpRequest,
  RefreshTokenRequest, RefreshTokenResponse,
  LogoutRequest, MessageResponse,
  CompleteProfileRequest, CompleteProfileResponse,
  ForgotPasswordRequest, ForgotPasswordVerifyRequest, ForgotPasswordVerifyResponse,
  ResetPasswordRequest, ChangePasswordRequest,
  UpdateProfileRequest, VendorUpdateProfileResponse,
  VendorMeResponse,
  CheckAvailabilityRequest, CheckAvailabilityResponse,
  RequestOptions,
} from '@cravo/core'
import { api } from '../api-client'

export const vendorAuthApi = {
  register(data: VendorRegisterRequest, options?: RequestOptions): Promise<VendorRegisterResponse> {
    return api.post<VendorRegisterResponse>('/vendor/auth/register', data, { skipAuth: true, ...options })
  },

  login(data: VendorLoginRequest, options?: RequestOptions): Promise<VendorLoginResponse> {
    return api.post<VendorLoginResponse>('/vendor/auth/login', data, { skipAuth: true, ...options })
  },

  socialLogin(data: VendorSocialLoginRequest, options?: RequestOptions): Promise<VendorSocialLoginResponse> {
    return api.post<VendorSocialLoginResponse>('/vendor/auth/social-login', data, { skipAuth: true, ...options })
  },

  verifyOtp(data: VerifyOtpRequest, options?: RequestOptions): Promise<VendorVerifyOtpResponse> {
    return api.post<VendorVerifyOtpResponse>('/vendor/auth/verify-otp', data, { skipAuth: true, ...options })
  },

  sendOtp(data: SendOtpRequest, options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/send-otp', data, { skipAuth: true, ...options })
  },

  resendOtp(data: ResendOtpRequest, options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/resend-otp', data, { skipAuth: true, ...options })
  },

  refreshToken(data: RefreshTokenRequest, options?: RequestOptions): Promise<RefreshTokenResponse> {
    return api.post<RefreshTokenResponse>('/vendor/auth/refresh-token', data, { skipAuth: true, skipRefresh: true, ...options })
  },

  logout(data?: LogoutRequest, options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/logout', data, options)
  },

  me(options?: RequestOptions): Promise<VendorMeResponse> {
    return api.get<VendorMeResponse>('/vendor/auth/me', options)
  },

  updateProfile(data: UpdateProfileRequest, options?: RequestOptions): Promise<VendorUpdateProfileResponse> {
    return api.put<VendorUpdateProfileResponse>('/vendor/auth/profile', data, options)
  },

  completeProfile(data: CompleteProfileRequest, options?: RequestOptions): Promise<CompleteProfileResponse> {
    return api.put<CompleteProfileResponse>('/vendor/auth/complete-profile', data, options)
  },

  checkAvailability(data: CheckAvailabilityRequest, options?: RequestOptions): Promise<CheckAvailabilityResponse> {
    return api.post<CheckAvailabilityResponse>('/vendor/auth/check-availability', data, { skipAuth: true, ...options })
  },

  forgotPassword(data: ForgotPasswordRequest, options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/forgot-password', data, { skipAuth: true, ...options })
  },

  forgotPasswordVerify(data: ForgotPasswordVerifyRequest, options?: RequestOptions): Promise<ForgotPasswordVerifyResponse> {
    return api.post<ForgotPasswordVerifyResponse>('/vendor/auth/forgot-password/verify', data, { skipAuth: true, ...options })
  },

  resetPassword(data: ResetPasswordRequest, resetToken: string, options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/reset-password', data, {
      skipRefresh: true,
      ...options,
      headers: { ...options?.headers, Authorization: `Bearer ${resetToken}` },
    })
  },

  changePassword(data: ChangePasswordRequest, options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/change-password', data, options)
  },

  getAccountStatistics(options?: RequestOptions): Promise<AccountStatistics> {
    return api.get<AccountStatistics>('/vendor/auth/account/statistics', options)
  },

  deleteAccount(
    data: { reason?: string; password?: string } | undefined,
    options?: RequestOptions,
  ): Promise<DeleteAccountResponse> {
    return api.delete<DeleteAccountResponse>('/vendor/auth/account', { ...options, data })
  },

  cancelDeletion(options?: RequestOptions): Promise<MessageResponse> {
    return api.post<MessageResponse>('/vendor/auth/account/cancel-deletion', undefined, options)
  },

  presignProfileUpload(
    data: { fileName: string; contentType: string },
    options?: RequestOptions,
  ): Promise<PresignResponse> {
    return api.post<PresignResponse>('/uploads/presign', { ...data, folder: 'profiles' }, options)
  },
}

export interface AccountStatistics {
  accountCreatedAt: string
  totalOrders?: number
  activeBusinesses?: number
  accountStatus?: string
  deletionScheduledAt: string | null
}

export interface DeleteAccountResponse {
  message: string
  scheduledDeletionAt: string
}

export interface PresignResponse {
  uploadUrl: string
  key: string
  publicUrl: string
  expiresIn: number
}
